<script setup lang="ts">
import type { TDocumentRejectionReason } from "@/api/types";
import type { TTempDocument } from "@/assets/js/globalTypes";
import { useComplianceDocuments } from "@/store/clientsDocuments";

const clientDocumentsStore = useComplianceDocuments();

const emit = defineEmits([
	"close-modal",
	"confirm",
	"select-reject-reason",
	"change-rejection-note",
]);
const props = withDefaults(
	defineProps<{
		doc: TTempDocument;
		rejectionReasons: TDocumentRejectionReason[];
		confirmBtnTxt?: string;
		cancelBtnTxt?: string;
		showConfirmBtn?: boolean;
		showCancelBtn?: boolean;
		confirmBtnClass?: string;
		warning?: boolean;
		isError?: boolean;
	}>(),
	{
		confirmBtnTxt: "Confirm",
		cancelBtnTxt: "Cancel",
		showConfirmBtn: true,
		showCancelBtn: true,
		confirmBtnClass: "",
		warning: false,
		isError: false,
	},
);

function onClickConfirm() {
	emit("confirm");
	emit("close-modal");
}
function onClickReturn() {
	emit("close-modal");
}
const confirmBtnDisabled = ref<boolean>(true);
function onSelectRejectReason(rejectionReason: TDocumentRejectionReason) {
	confirmBtnDisabled.value = false;
	emit("select-reject-reason", rejectionReason);
}

const rejectionNote = ref("");
function onChangeRejectionNote() {
	emit("change-rejection-note", rejectionNote.value);
}
</script>

<template>
	<div
		class="confirm-modal"
		:class="{ warning: warning }"
	>
		<div class="content">
			<div>
				<label class="label">Please select a reason for rejecting</label>
				<template v-if="props.rejectionReasons">
					<multiselect-form
						:options="props.rejectionReasons"
						label="name"
						placeholder="Rejection reason"
						value-prop="id"
						:multiple="false"
						:showLabels="true"
						:searchable="true"
						:canClear="false"
						:canDeselect="false"
						style="width: 350px; border-radius: 21px; height: 40px"
						@select="onSelectRejectReason"
					/>
					<div class="flex-space-between wd-100-p">
						<label class="label">Note</label>
						<label class="label">
							Characters left:
							{{ clientDocumentsStore.rejectionNoteCharactersLeftCount }}
						</label>
					</div>
					<textarea
						v-model="rejectionNote"
						style="width: 100%; height: 120px; padding-left: 10px; resize: none"
						rows="10"
						:maxlength="clientDocumentsStore.rejectionNoteMaxLength"
						placeholder="Type here..."
						@input="onChangeRejectionNote"
					></textarea>
				</template>
				<div
					v-else
					class="loading-wrap"
				>
					<div class="loading-wrapper flex-center">
						<img
							class="wd-50"
							src="@/assets/image/loader.svg"
						/>
					</div>
				</div>
			</div>
			<div class="wrapp-buttons">
				<button
					v-if="showCancelBtn"
					class="alt"
					@click="onClickReturn()"
				>
					{{ cancelBtnTxt }}
				</button>
				<button
					v-if="showConfirmBtn"
					class="btn"
					:class="{ 'not-allowed': confirmBtnDisabled }"
					:disabled="confirmBtnDisabled"
					@click="onClickConfirm()"
				>
					{{ confirmBtnTxt }}
				</button>
			</div>
		</div>
	</div>
</template>
<style lang="scss" scoped>
.confirm-modal {
	padding: 0 60px 45px;
	display: flex;
	flex-direction: column;
	align-items: center;
	max-width: 600px;
	min-height: 130px;
	gap: 10px;
	color: $text-color;

	.content {
		margin-top: 25px;
		display: flex;
		width: 100%;
		flex-direction: column;
		align-items: center;
		justify-content: space-around;
		gap: 10px;

		.warningTxt {
			font-size: 2em;
		}
		.message {
			text-align: center;
			font-size: 1rem;
			// color: $success-color;
			transition: 2s;
		}
		.isError {
			color: $error-color;
		}
	}
	.wrapp-buttons {
		margin-top: 25px;
		display: flex;
		max-width: 100%;
		// flex-direction: column;
		align-items: center;
		justify-content: space-around;
		gap: 10px;
	}
}
.confirm-modal.warning {
	max-width: 400px;
}
</style>
